import React, { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSession, sessionActions } from './SessionContext';
import { getStoredLicenseKeyHash } from '../services/LicenseService';

/**
 * Clears the editor's session state (active PDF, drawn fields, session
 * metadata, cached session list) whenever the underlying license or
 * authenticated user changes.
 *
 * <h2>Why this exists</h2>
 * Without it, the editor leaks state across customer / license / auth
 * boundaries: user A signs in, uploads a PDF, draws fields, signs out;
 * user B signs in on the same browser and briefly sees user A's PDF and
 * fields before the new workflow loads. This is the UI-layer companion
 * to the API-layer cross-customer-license-activation block.
 *
 * <h2>Trigger points</h2>
 * <ol>
 *   <li>A {@code license-changed} window event is dispatched by
 *       LicenseService on license apply, license clear, and trial
 *       cancellation. Re-applying the SAME license is intentionally a
 *       no-op (no work loss).</li>
 *   <li>Authentication transitions: authenticated → unauthenticated
 *       (logout, AUTH_FAILURE after being signed in) OR a userId change
 *       while still authenticated (rare, but covers the edge case of
 *       two users signing in sequentially without a logout step).</li>
 * </ol>
 *
 * <h2>Why a bridge component rather than inline in a context</h2>
 * Keeps SessionContext from importing AuthContext (or vice versa) —
 * separation of concerns. The cross-cutting "reset on auth change"
 * concern sits in its own component mounted at the provider intersection.
 *
 * @returns {@code null} — renders nothing, exists for its side effects only.
 */
export const EditorResetBridge: React.FC = () => {
  const { state: authState } = useAuth();
  const { dispatch } = useSession();

  // Refs survive re-renders so we can detect transitions across them.
  const wasAuthenticated = useRef(authState.isAuthenticated);
  const lastUserId = useRef<number | null>(authState.user?.userId ?? null);
  const lastLicenseHash = useRef<string | null>(getStoredLicenseKeyHash());

  // license-changed events: license apply, license clear, trial cancel.
  // Guard against the same-license re-apply case so users don't lose
  // unsaved work by clicking Apply on a key already in effect.
  useEffect(() => {
    const handler = () => {
      const currentHash = getStoredLicenseKeyHash();
      if (currentHash !== lastLicenseHash.current) {
        dispatch(sessionActions.clearSession());
        lastLicenseHash.current = currentHash;
      }
    };
    window.addEventListener('license-changed', handler);
    return () => window.removeEventListener('license-changed', handler);
  }, [dispatch]);

  // Auth transitions that warrant a session wipe.
  useEffect(() => {
    const becameUnauth =
      wasAuthenticated.current && !authState.isAuthenticated;

    const userChanged =
      authState.isAuthenticated &&
      lastUserId.current != null &&
      authState.user?.userId != null &&
      authState.user.userId !== lastUserId.current;

    if (becameUnauth || userChanged) {
      dispatch(sessionActions.clearSession());
    }

    // Update refs for next-render transition detection.
    wasAuthenticated.current = authState.isAuthenticated;
    if (authState.user?.userId != null) {
      lastUserId.current = authState.user.userId;
    }
  }, [authState.isAuthenticated, authState.user?.userId, dispatch]);

  return null;
};

export default EditorResetBridge;
